import React, { useMemo, useState } from "react";
import "./MonthCalendar.scss";

import { ReactComponent as ChevronLeftIcon } from "../../../assets/icons/patient-layout-v2/consults/ChevronLeftIcon.svg";
import { ReactComponent as ChevronRightIcon } from "../../../assets/icons/patient-layout-v2/consults/ChevronRightIcon.svg";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const toDateValue = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

// Six rows of seven, so the grid does not change height as months change.
const buildGrid = (year: number, month: number) => {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());

  return Array.from({ length: 42 }, (_, offset) => {
    const date = new Date(start);
    date.setDate(start.getDate() + offset);
    return date;
  });
};

interface MonthCalendarProps {
  /** Selected day as "YYYY-MM-DD". */
  value: string;
  onChange: (value: string) => void;
  /** Earliest selectable day; defaults to today, since the past cannot be booked. */
  minDate?: Date;
  maxDate?: Date;
}

const MonthCalendar: React.FC<MonthCalendarProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
}) => {
  const selected = useMemo(() => {
    const [year, month, day] = (value || "").split("-").map(Number);
    return year ? new Date(year, month - 1, day) : new Date();
  }, [value]);

  const [cursor, setCursor] = useState(
    () => new Date(selected.getFullYear(), selected.getMonth(), 1),
  );

  // Compared as day boundaries: a slot later today is still bookable.
  const floor = useMemo(() => {
    const date = minDate ? new Date(minDate) : new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, [minDate]);

  const ceiling = useMemo(() => {
    if (!maxDate) return null;
    const date = new Date(maxDate);
    date.setHours(23, 59, 59, 999);
    return date;
  }, [maxDate]);

  const days = useMemo(
    () => buildGrid(cursor.getFullYear(), cursor.getMonth()),
    [cursor],
  );

  const shiftMonth = (step: number) =>
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + step, 1));

  return (
    <div className="month-calendar">
      <div className="mc-head">
        <button
          type="button"
          className="mc-nav"
          onClick={() => shiftMonth(-1)}
          aria-label="Previous month"
        >
          <ChevronLeftIcon />
        </button>
        <div className="mc-month">
          {MONTHS[cursor.getMonth()]}
          {cursor.getFullYear() !== new Date().getFullYear() && (
            <span className="mc-year"> {cursor.getFullYear()}</span>
          )}
        </div>
        <button
          type="button"
          className="mc-nav"
          onClick={() => shiftMonth(1)}
          aria-label="Next month"
        >
          <ChevronRightIcon />
        </button>
      </div>

      <div className="mc-grid">
        {WEEKDAYS.map((weekday) => (
          <div className="mc-weekday" key={weekday}>
            {weekday}
          </div>
        ))}

        {days.map((day) => {
          const dayValue = toDateValue(day);
          const outside = day.getMonth() !== cursor.getMonth();
          const disabled =
            day < floor || (!!ceiling && day > ceiling);

          return (
            <button
              key={dayValue}
              type="button"
              className={`mc-day ${outside ? "outside" : ""} ${
                dayValue === value ? "selected" : ""
              }`}
              disabled={disabled}
              onClick={() => onChange(dayValue)}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MonthCalendar;
